import { useState } from 'react';
import {
  Button,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { Add, MoreVert, CheckCircle, Cancel } from '@mui/icons-material';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import { ConsolidatedSkill } from '@/services/workersV2API';
import AddWorkExperienceModal from './AddWorkExperienceModal';
import UploadUniformModal from './UploadUniformModal';
import UploadCertificateModal from './UploadCertificateModal';
import ExpirationSection from './ExpirationSection';
import CertificateReviewSection from './CertificateReviewSection';
import QuizAssessmentSection from './QuizAssessmentSection';
import AIAssessmentSection from './AIAssessmentSection';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  skill: ConsolidatedSkill;
  workerId: number;
  onUpdate: () => void;
};

const SkillPanel = ({ skill, workerId, onUpdate }: Props) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUniformModal, setShowUniformModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [editingExperience, setEditingExperience] = useState<number | null>(
    null,
  );
  const [menuAnchor, setMenuAnchor] = useState<{
    element: HTMLElement;
    experienceId: number;
  } | null>(null);
  const [processingAction, setProcessingAction] = useState(false);

  // Dialog states
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: '',
    message: '',
  });
  const [deleteSkillDialog, setDeleteSkillDialog] = useState<{
    open: boolean;
    markAsIneligible: boolean;
  }>({
    open: false,
    markAsIneligible: true,
  });
  const [rejectUniformDialog, setRejectUniformDialog] = useState<{
    open: boolean;
    reason: string;
  }>({
    open: false,
    reason: '',
  });

  const handleAddExperience = () => {
    setEditingExperience(null);
    setShowAddModal(true);
  };

  const handleExperienceAdded = () => {
    setShowAddModal(false);
    setEditingExperience(null);
    onUpdate();
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    experienceId: number,
  ) => {
    setMenuAnchor({ element: event.currentTarget, experienceId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditExperience = () => {
    if (menuAnchor) {
      setEditingExperience(menuAnchor.experienceId);
      setShowAddModal(true);
    }
    handleMenuClose();
  };

  const handleDeleteExperience = () => {
    if (!menuAnchor) return;
    handleMenuClose();

    setConfirmDialog({
      open: true,
      title: 'Delete Work Experience',
      message: 'Are you sure you want to delete this work experience?',
      onConfirm: async () => {
        try {
          const response = await axiosRubyClient.delete(
            `/api/admin/work_experiences/${menuAnchor.experienceId}`,
          );

          if (response.data.success) {
            onUpdate();
          } else {
            setAlertDialog({
              open: true,
              title: 'Error',
              message: 'Failed to delete work experience',
            });
          }
        } catch (error) {
          console.error('Error deleting work experience:', error);
          setAlertDialog({
            open: true,
            title: 'Error',
            message: 'Failed to delete work experience',
          });
        }
      },
    });
  };

  const handleApproveUniform = async () => {
    if (!skill.skill_request?.uniforms?.[0]) return;

    setProcessingAction(true);

    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_uniforms/${skill.skill_request.uniforms[0].id}/accept`,
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to approve uniform',
        });
      }
    } catch (error) {
      console.error('Error approving uniform:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to approve uniform',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectUniform = async () => {
    if (!skill.skill_request?.uniforms?.[0]) return;

    setProcessingAction(true);
    setRejectUniformDialog({ open: false, reason: '' });

    try {
      const response = await axiosRubyClient.post(
        `/api/admin/worker_uniforms/${skill.skill_request.uniforms[0].id}/reject`,
        { comment: rejectUniformDialog.reason || undefined },
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to reject uniform',
        });
      }
    } catch (error) {
      console.error('Error rejecting uniform:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to reject uniform',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleReplaceUniform = () => {
    setShowUniformModal(true);
  };

  const handleDeleteSkill = () => {
    if (!skill.worker_skill) return;
    setDeleteSkillDialog({ open: true, markAsIneligible: true });
  };

  const handleConfirmDeleteSkill = async () => {
    if (!skill.worker_skill) return;

    setDeleteSkillDialog({ ...deleteSkillDialog, open: false });
    setProcessingAction(true);

    try {
      const response = await axiosRubyClient.delete(
        `/api/admin/worker_skills/${skill.worker_skill.id}`,
        {
          data: {
            mark_as_ineligible: deleteSkillDialog.markAsIneligible,
          },
        },
      );

      if (response.data.success) {
        onUpdate();
      } else {
        setAlertDialog({
          open: true,
          title: 'Error',
          message: 'Failed to remove skill from worker',
        });
      }
    } catch (error) {
      console.error('Error removing skill:', error);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: 'Failed to remove skill from worker',
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleError = (title: string, message: string) => {
    setAlertDialog({ open: true, title, message });
  };

  const isApproved = skill.status === 'approved';

  const uniform = skill.skill_request?.uniforms?.[0];
  const uniformStatus = uniform?.status;

  const getUniformStatusConfig = () => {
    if (uniformStatus === 'accepted') {
      return {
        bgColor: '#ECFDF5',
        borderColor: '#A7F3D0',
        label: 'Approved',
        badgeStatus: 'theme' as const,
      };
    }
    if (uniformStatus === 'rejected') {
      return {
        bgColor: '#FEF2F2',
        borderColor: '#FECACA',
        label: 'Rejected',
        badgeStatus: 'danger' as const,
      };
    }
    return {
      bgColor: '#FFFBEB',
      borderColor: '#FDE68A',
      label: 'Pending Review',
      badgeStatus: 'warning' as const,
    };
  };

  return (
    <Stack vertical gap={24} style={{ width: '100%' }}>
      {/* Expiration Section - only show if not approved */}
      {skill.worker_skill && !isApproved && (
        <Card>
          <Card.Section>
            <ExpirationSection
              workerSkillId={skill.worker_skill.id}
              expiresAt={skill.worker_skill.expires_at}
              daysUntilExpiration={skill.worker_skill.days_until_expiration}
              expired={skill.worker_skill.expired}
              onUpdate={onUpdate}
            />
          </Card.Section>
        </Card>
      )}

      {/* Work Experience Section */}
      <Card>
        <Card.Section>
          <Stack
            style={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <Stack gap={8} style={{ alignItems: 'center' }}>
              <div
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#10B981',
                }}
              />
              <Body weight="semibold">Work Experience</Body>
            </Stack>
            <Button size="small" onClick={handleAddExperience}>
              Add
            </Button>
          </Stack>

          {skill.skill_request?.work_experiences &&
          skill.skill_request.work_experiences.length > 0 ? (
            <Stack vertical gap={16}>
              {[...skill.skill_request.work_experiences]
                .sort(
                  (a, b) =>
                    new Date(b.start_date).getTime() -
                    new Date(a.start_date).getTime(),
                )
                .map((exp) => (
                  <Stack
                    key={exp.id}
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <Stack vertical gap={4}>
                      <Body weight="semibold">{exp.company_name}</Body>
                      <Small color="lighter">
                        {(() => {
                          const [year, month] = exp.start_date.split('-');
                          return new Date(
                            parseInt(year),
                            parseInt(month) - 1,
                          ).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric',
                          });
                        })()}{' '}
                        -{' '}
                        {exp.end_date
                          ? (() => {
                              const [year, month] = exp.end_date.split('-');
                              return new Date(
                                parseInt(year),
                                parseInt(month) - 1,
                              ).toLocaleDateString('en-US', {
                                month: 'short',
                                year: 'numeric',
                              });
                            })()
                          : 'Present'}
                      </Small>
                      {exp.is_staffing_agency && (
                        <Small color="lighter">Through Staffing agency</Small>
                      )}
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, exp.id)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
            </Stack>
          ) : (
            <Small color="lighter">No work experience added yet</Small>
          )}
        </Card.Section>
      </Card>

      {/* Work Experience Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditExperience}>Edit</MenuItem>
        <MenuItem onClick={handleDeleteExperience} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>

      {/* Quiz Assessment Section */}
      {skill.requires_assessment && skill.skill_request && (
        <QuizAssessmentSection
          skillRequestId={skill.skill_request.id}
          assessment={skill.skill_request.assessment}
          assessmentPassed={skill.skill_request.assessment_passed}
          canRetry={skill.skill_request.can_retry_assessment}
          retryMessage={skill.skill_request.retry_message}
          workerId={workerId}
          skillId={skill.skill_id}
          onUpdate={onUpdate}
          onError={handleError}
        />
      )}

      {/* AI Assessment Section */}
      {skill.ai_assessment && skill.skill_request && (
        <AIAssessmentSection
          aiAssessment={skill.ai_assessment}
          canRetry={skill.skill_request.can_retry_assessment}
          skillRequestId={skill.skill_request.id}
          onUpdate={onUpdate}
          onError={handleError}
        />
      )}

      {/* Uniform Section */}
      {skill.requires_uniform && skill.skill_request && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                  }}
                />
                <Body weight="semibold">Black & White Uniform</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={16}>
              <Small color="lighter">
                Clean and pressed. White long-sleeve button up shirt, black
                slacks, black socks, black non-slip shoes.
              </Small>

              {uniform ? (
                <div
                  style={{
                    backgroundColor: getUniformStatusConfig().bgColor,
                    border: `1px solid ${getUniformStatusConfig().borderColor}`,
                    borderRadius: '12px',
                    padding: '16px',
                  }}
                >
                  {/* Status Header */}
                  <Stack
                    style={{
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <Small weight="semibold">Submitted Uniform</Small>
                    <Badge
                      label={getUniformStatusConfig().label}
                      status={getUniformStatusConfig().badgeStatus}
                    />
                  </Stack>

                  {/* Uniform Image */}
                  <img
                    src={uniform.image_url || ''}
                    alt="Worker uniform"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      backgroundColor: '#fff',
                      border: '1px solid #E5E7EB',
                    }}
                  />

                  {/* Metadata */}
                  <Stack vertical gap={8} style={{ marginBottom: '16px' }}>
                    <Stack style={{ justifyContent: 'space-between' }}>
                      <Small color="lighter">Uploaded</Small>
                      <Small>
                        {new Date(uniform.created_at).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          },
                        )}
                      </Small>
                    </Stack>
                  </Stack>

                  {/* Action Buttons */}
                  {uniformStatus !== 'accepted' &&
                    uniformStatus !== 'rejected' && (
                      <Stack gap={12}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={
                            processingAction ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CheckCircle />
                            )
                          }
                          onClick={handleApproveUniform}
                          disabled={processingAction}
                          style={{ flex: 1 }}
                        >
                          {processingAction
                            ? 'Approving...'
                            : 'Approve Uniform'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<Cancel />}
                          onClick={() =>
                            setRejectUniformDialog({ open: true, reason: '' })
                          }
                          disabled={processingAction}
                          style={{ flex: 1 }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    )}

                  {/* Already approved */}
                  {uniformStatus === 'accepted' && (
                    <Stack vertical gap={8}>
                      <Stack
                        gap={8}
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                        }}
                      >
                        <CheckCircle
                          style={{ color: '#10B981', fontSize: '18px' }}
                        />
                        <Small color="lighter">Uniform has been approved</Small>
                      </Stack>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleReplaceUniform}
                      >
                        Replace Uniform Photo
                      </Button>
                    </Stack>
                  )}

                  {/* Already rejected */}
                  {uniformStatus === 'rejected' && (
                    <Stack vertical gap={8}>
                      <Stack
                        gap={8}
                        style={{
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '8px',
                          backgroundColor: '#fff',
                          borderRadius: '6px',
                        }}
                      >
                        <Cancel
                          style={{ color: '#EF4444', fontSize: '18px' }}
                        />
                        <Small color="lighter">Uniform has been rejected</Small>
                      </Stack>
                      <Stack gap={8}>
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={
                            processingAction ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CheckCircle />
                            )
                          }
                          onClick={handleApproveUniform}
                          disabled={processingAction}
                          style={{ flex: 1 }}
                        >
                          Approve Instead
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={handleReplaceUniform}
                          style={{ flex: 1 }}
                        >
                          Replace Photo
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </div>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setShowUniformModal(true)}
                >
                  Upload Uniform Photo
                </Button>
              )}
            </Stack>
          </Card.Section>
        </Card>
      )}

      {/* Certificate Section */}
      {skill.requires_certificate && skill.skill_request && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#6B7280',
                  }}
                />
                <Body weight="semibold">Certificates</Body>
              </Stack>
              <Button
                size="small"
                onClick={() => setShowCertificateModal(true)}
              >
                Add
              </Button>
            </Stack>

            {skill.skill_request.certificates &&
            skill.skill_request.certificates.length > 0 ? (
              <Stack vertical gap={0}>
                {skill.skill_request.certificates.map((cert, index) => (
                  <CertificateReviewSection
                    key={cert.id}
                    certificate={cert}
                    index={index}
                    totalCount={skill.skill_request!.certificates!.length}
                    onUpdate={onUpdate}
                    onError={handleError}
                  />
                ))}
              </Stack>
            ) : (
              <Small color="lighter">No certificates uploaded yet</Small>
            )}
          </Card.Section>
        </Card>
      )}

      {/* Remove Skill Section */}
      {skill.worker_skill && (
        <Card>
          <Card.Section>
            <Stack
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <Stack gap={8} style={{ alignItems: 'center' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                  }}
                />
                <Body weight="semibold">Danger Zone</Body>
              </Stack>
            </Stack>

            <Stack vertical gap={12}>
              <Small color="lighter">
                Removing this skill will prevent the worker from being assigned
                to jobs requiring this skill.
              </Small>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleDeleteSkill}
                disabled={processingAction}
              >
                {processingAction ? 'Removing...' : 'Remove Skill from Worker'}
              </Button>
            </Stack>
          </Card.Section>
        </Card>
      )}

      <AddWorkExperienceModal
        open={showAddModal}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        experienceId={editingExperience}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleExperienceAdded}
      />
      <UploadUniformModal
        open={showUniformModal}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        onClose={() => setShowUniformModal(false)}
        onSuccess={() => {
          setShowUniformModal(false);
          onUpdate();
        }}
      />
      <UploadCertificateModal
        open={showCertificateModal}
        workerId={workerId}
        skillId={skill.skill_id}
        skillRequestId={skill.skill_request?.id}
        certificateType={
          skill.skill_request?.certificates?.[0]?.type || undefined
        }
        onClose={() => setShowCertificateModal(false)}
        onSuccess={() => {
          setShowCertificateModal(false);
          onUpdate();
        }}
      />

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm();
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <Dialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{alertDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{alertDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAlertDialog({ ...alertDialog, open: false })}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Skill Dialog */}
      <Dialog
        open={deleteSkillDialog.open}
        onClose={() =>
          setDeleteSkillDialog({ ...deleteSkillDialog, open: false })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove Skill from Worker</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Are you sure you want to remove "{skill.skill_name}" from this
            worker? This action cannot be undone.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteSkillDialog.markAsIneligible}
                onChange={(e) =>
                  setDeleteSkillDialog({
                    ...deleteSkillDialog,
                    markAsIneligible: e.target.checked,
                  })
                }
                color="primary"
              />
            }
            label="Also mark as ineligible for this skill"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteSkillDialog({ ...deleteSkillDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSkill}
            variant="contained"
            color="error"
          >
            Remove Skill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Uniform Dialog */}
      <Dialog
        open={rejectUniformDialog.open}
        onClose={() => setRejectUniformDialog({ open: false, reason: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Uniform</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Please provide a reason for rejecting this uniform (optional). This
            will be visible to the worker.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            value={rejectUniformDialog.reason}
            onChange={(e) =>
              setRejectUniformDialog({
                ...rejectUniformDialog,
                reason: e.target.value,
              })
            }
            placeholder="e.g., Shirt is wrinkled, wrong color pants..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRejectUniformDialog({ open: false, reason: '' })}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectUniform}
            variant="contained"
            color="error"
            disabled={processingAction}
          >
            {processingAction ? 'Rejecting...' : 'Reject Uniform'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SkillPanel;
